import React from 'react';
import { formatPrice } from '../utils/money-utils';
import { describeCoreFilters, describeFlagFilters, DescribableFilters } from '../utils/subscriptionDescription';

export interface SubscriptionSummaryProps {
  zipCount: number;
  maxRent?: number | null;
  minRooms?: number | null;
  minArea?: number | null;
  channels: string[];
  /** When given, the summary shows full ranges (incl. Kaltmiete) and, from md up, the
   * yes/no filters — the same description as the search overview. */
  filters?: DescribableFilters;
  /** Named areas, e.g. "Berlin Mitte (3 PLZ), Berlin Pankow"; replaces the bare PLZ count. */
  areaLabel?: string | null;
}

const CHANNEL_LABELS: Record<string, string> = {
  telegram: 'Telegram',
  push: 'Push',
  email: 'E-Mail',
};

function formatRoomsValue(rooms: number): string {
  return Number.isInteger(rooms) ? String(rooms) : rooms.toLocaleString('de-DE', { minimumFractionDigits: 1, maximumFractionDigits: 1 });
}

/**
 * Live summary shown in the sticky action bar, in the fact-line style of the
 * push format: "3 PLZ | bis 1.200 € | ab 2 Zi. | ab 50 m² · per Telegram".
 */
export const SubscriptionSummary: React.FC<SubscriptionSummaryProps> = ({ zipCount, maxRent, minRooms, minArea, channels, filters, areaLabel }) => {
  if (zipCount === 0) {
    return <p className="text-sm text-muted">Wähle mindestens eine PLZ.</p>;
  }

  if (channels.length === 0) {
    return <p className="tabular text-sm font-medium text-ink">Keine Meldungen eingestellt.</p>;
  }

  const parts: string[] = [areaLabel || `${zipCount} PLZ`];
  if (filters) {
    parts.push(...describeCoreFilters(filters));
  } else {
    if (maxRent != null) parts.push(`bis ${formatPrice(maxRent)}`);
    if (minRooms != null) parts.push(`ab ${formatRoomsValue(minRooms)} Zi.`);
    if (minArea != null) parts.push(`ab ${minArea} m²`);
  }
  const flags = filters ? describeFlagFilters(filters) : [];

  let text = `Du bekommst Meldungen für: ${parts.join(' | ')}`;
  const channelLabel = channels.length === 1 ? CHANNEL_LABELS[channels[0]] : undefined;
  const channelText = channelLabel ? `, per ${channelLabel}` : '';

  return (
    <p className="tabular text-sm font-medium text-ink">
      {text}
      {flags.length > 0 && <span className="hidden md:inline"> | {flags.join(', ')}</span>}
      {channelText}
    </p>
  );
};
